import type { UseFormReturn } from 'react-hook-form';

import { BoothOtpMode } from '@cibli/shared';
import React, { createContext, useMemo } from 'react';

import { useBooth } from '@/features/settings/hooks/useBooth';

import { type CGUFormData, useCGUForm } from '../hooks/useCGUForm';
import { type CityFormData, useCityForm } from '../hooks/useCityForm';
import {
  type NamesFormData,
  NamesFormInput,
  NamesFormOutput,
  useNamesForm,
} from '../hooks/useNamesForm';
import { type PhoneFormData, usePhoneForm } from '../hooks/usePhoneForm';

// Combined user data type
export type UserInfo = NamesFormData &
  PhoneFormData &
  CGUFormData &
  CityFormData & { paymentIntentId?: string };

export type RegisterContextType = {
  // Step hooks
  namesForm: UseFormReturn<NamesFormInput, unknown, NamesFormOutput>;
  phoneForm: UseFormReturn<PhoneFormData>;
  cguForm: UseFormReturn<CGUFormData>;
  cityForm: UseFormReturn<CityFormData>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const RegisterContext = createContext<RegisterContextType | null>(null);

type RegisterProviderProps = {
  children: React.ReactNode;
  onEndForm: (userInfo: UserInfo) => void | Promise<void>;
  initialData?: Partial<UserInfo>;
};

export const RegisterProvider = ({ children, initialData }: RegisterProviderProps) => {
  const { booth } = useBooth();
  // Booth OTP mode drives which contact details are mandatory in the register form
  const otpMode = booth?.otpMode ?? BoothOtpMode.SMS;
  const emailRequired = otpMode === BoothOtpMode.EMAIL;
  const phoneRequired = otpMode !== BoothOtpMode.EMAIL;

  const { form: namesForm } = useNamesForm(initialData, emailRequired);
  const { form: phoneForm } = usePhoneForm(initialData, phoneRequired);
  const { form: cguForm } = useCGUForm(initialData);
  const { form: cityForm } = useCityForm(initialData);

  const contextValue: RegisterContextType = useMemo(
    () => ({
      namesForm,
      phoneForm,
      cguForm,
      cityForm,
    }),
    [namesForm, phoneForm, cguForm, cityForm],
  );

  return <RegisterContext.Provider value={contextValue}>{children}</RegisterContext.Provider>;
};
